import { mapValues, invert, difference, min, max, flatten, uniq, range } from 'lodash-es'

import pqTypes from '@/utilities/pq-types'
import * as graph from '@shared/types/graphql'
import * as db from '@shared/types/database'
import { SelectOption } from '@alpha/ink'

export enum AudienceCriteriaType {
  AGE = 'Age',
  EDUCATION_LEVEL = 'Education',
  GENDER = 'Gender',
  US_HHI = 'Household Income',
  PARENTAL_STATUS = 'Parental Status',
  RELATIONSHIP_STATUS = 'Relationship Status',
  HOUSEHOLD_TYPE = 'Household Type',

  EMPLOYMENT = 'Employment',
  INDUSTRY = 'Industry',
  NUMBER_OF_EMPLOYEES = '# of Employees',
  STANDARD_COMPANY_DEPARTMENT = 'Company Dept.',
  JOB_TITLE = 'Job Title',
  B2B_DECISIONS = 'B2B Decisions',

  DEVICE = 'Device',
  BIG_PURCHASES = 'Big Purchases',

  ADDITIONAL = 'Additional Criteria',
  GEOGRAPHIC_LOCATION = 'Geographic Location'
}

const audienceCriteriaTypesByLucidKey = invert(mapValues(pqTypes, criteria => criteria.lucidKey)) as Record<string, AudienceCriteriaType>

type NumberRange = [number, number]
type OptionValues = string[]

export enum GeographicLocationType {
  INTERNATIONAL = 'international',
  USA = 'usa',
  USA_STATES = 'usa_states'
}

export type GeographicLocation = {
  type: GeographicLocationType.INTERNATIONAL
  countries: string[]
} | {
  type: GeographicLocationType.USA_STATES
  states: string[]
} | {
  type: GeographicLocationType.USA
}

export type FilterOptionGroups = {
  order: string,
  titles: { name: string }[]
}

export type FilterDetails = Record<Exclude<AudienceCriteriaType, AudienceCriteriaType.AGE | AudienceCriteriaType.GEOGRAPHIC_LOCATION>, {
  name: string,
  type: string,
  description: string,
  screener: string,
  options?: string[] | FilterOptionGroups[] | number[] | [Array<SelectOption>, Array<SelectOption>]
}>
export interface AudienceCriteria {
  [AudienceCriteriaType.ADDITIONAL]?: string

  [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: GeographicLocation

  [AudienceCriteriaType.AGE]: NumberRange
  [AudienceCriteriaType.US_HHI]?: NumberRange

  [AudienceCriteriaType.EDUCATION_LEVEL]?: OptionValues
  [AudienceCriteriaType.GENDER]?: OptionValues
  [AudienceCriteriaType.PARENTAL_STATUS]?: OptionValues
  [AudienceCriteriaType.RELATIONSHIP_STATUS]?: OptionValues
  [AudienceCriteriaType.HOUSEHOLD_TYPE]?: OptionValues
  [AudienceCriteriaType.EMPLOYMENT]?: OptionValues
  [AudienceCriteriaType.INDUSTRY]?: OptionValues
  [AudienceCriteriaType.NUMBER_OF_EMPLOYEES]?: OptionValues
  [AudienceCriteriaType.STANDARD_COMPANY_DEPARTMENT]?: OptionValues
  [AudienceCriteriaType.JOB_TITLE]?: OptionValues
  [AudienceCriteriaType.B2B_DECISIONS]?: OptionValues
  [AudienceCriteriaType.DEVICE]?: OptionValues
  [AudienceCriteriaType.BIG_PURCHASES]?: OptionValues
}

export default interface Audience {
  id: string
  name: string
  criteria: AudienceCriteria
}

type GraphAudience = Pick<graph.Audience, 'name' | '_id'> & {
  payload: {
    'account_id'?: string
    archived?: boolean
    segment?: {
      custom?: string
      visible?: boolean
      lucid?: Record<string, string[]>
      demoQualifies?: boolean
      countries?: string[]
    }
  }
}

function parseLucidPrecodes<T extends keyof typeof pqTypes> (criteriaTypeInfo: typeof pqTypes[T], precodes: string[]): OptionValues {
  return criteriaTypeInfo.options
    .filter(o => difference(o.precodes, precodes).length === 0)
    .map(o => o.title)
}

function getLucidPrecodesFromOptionValues<T extends keyof typeof pqTypes> (criteriaTypeInfo: typeof pqTypes[T], values: OptionValues): string[] {
  return flatten(uniq(
    criteriaTypeInfo.options
      .filter(o => values.includes(o.title))
      .map(o => o.precodes)
  ))
}

function getLucidPrecodesFromNumberRange (numberRange: NumberRange): string[] {
  const [start, end] = numberRange
  return range(start, end + 1).map(n => n.toString())
}

const lucidUSHHIBucketOffsets = [
  0,
  5000,
  10000,
  15000,
  20000,
  25000,
  30000,
  35000,
  40000,
  45000,
  50000,
  55000,
  60000,
  65000,
  70000,
  75000,
  80000,
  85000,
  90000,
  95000,
  100000,
  125000,
  150000,
  175000,
  200000,
  250000
]

function getLucidPrecodesFromUSHHINumberRange (numberRange: NumberRange): string[] {
  const [start, end] = numberRange
  if (start === Infinity) return []
  const startPrecode = lucidUSHHIBucketOffsets.findIndex(v => start && start <= v) + 1
  const endIndex = lucidUSHHIBucketOffsets.findIndex(v => end && end <= v)
  const endPrecode = endIndex === -1 ? 26 : endIndex
  return getLucidPrecodesFromNumberRange([startPrecode, endPrecode])
}

export function parseGraphAudience (audience: GraphAudience): Audience {
  const criteria: AudienceCriteria = {
    [AudienceCriteriaType.AGE]: [18, 75],
    [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: {
      type: GeographicLocationType.USA
    }
  }

  const { segment } = audience.payload
  if (segment) {
    // Additional criteria
    if (segment.custom) criteria[AudienceCriteriaType.ADDITIONAL] = segment.custom

    // Geographic location
    if (segment.countries) {
      criteria[AudienceCriteriaType.GEOGRAPHIC_LOCATION] = {
        type: GeographicLocationType.INTERNATIONAL,
        countries: segment.countries
      }
    } else if (segment.lucid?.STATE) {
      criteria[AudienceCriteriaType.GEOGRAPHIC_LOCATION] = {
        type: GeographicLocationType.USA_STATES,
        states: parseLucidPrecodes(pqTypes.State, segment.lucid.STATE)
      }
    }

    if (segment.lucid) {
      for (const lucidKey in segment.lucid) {
        if (lucidKey === 'STATE') continue

        const values = segment.lucid[lucidKey]
        const audienceCriteriaType = audienceCriteriaTypesByLucidKey[lucidKey]
        switch (audienceCriteriaType) {
          case AudienceCriteriaType.ADDITIONAL:
          case AudienceCriteriaType.GEOGRAPHIC_LOCATION:
            continue
        }
        const criteriaTypeInfo = pqTypes[audienceCriteriaType]
        if (audienceCriteriaType === AudienceCriteriaType.AGE) {
          const valueNumbers = values.map(v => parseInt(v))
          const minAge = min(valueNumbers)
          const maxAge = max(valueNumbers)
          if (minAge === undefined || maxAge === undefined) continue
          criteria[audienceCriteriaType] = [minAge, maxAge]
          continue
        }

        if (audienceCriteriaType === AudienceCriteriaType.US_HHI) {
          criteria[audienceCriteriaType] = parseLucidHHIPrecodeRange(values)
          continue
        }

        criteria[audienceCriteriaType] = parseLucidPrecodes(criteriaTypeInfo, values)
      }
    }
  }

  return {
    id: audience._id,
    name: audience.name,
    criteria
  }
}

export function formatAudienceForRR (audience: NewAudience): db.FormattedAudienceForRR {
  const geographicLocation = audience.criteria[AudienceCriteriaType.GEOGRAPHIC_LOCATION]
  const lucid: Record<string, string[]> = {}

  if (geographicLocation && geographicLocation.type === GeographicLocationType.USA_STATES) {
    lucid.STATE = getLucidPrecodesFromOptionValues(pqTypes.State, geographicLocation.states)
  }

  let criteriaType: AudienceCriteriaType
  for (criteriaType in audience.criteria) {
    switch (criteriaType) {
      case AudienceCriteriaType.GEOGRAPHIC_LOCATION:
      case AudienceCriteriaType.ADDITIONAL:
        break
      case AudienceCriteriaType.US_HHI: {
        const value = audience.criteria[criteriaType]
        if (!value) break
        lucid[pqTypes[criteriaType].lucidKey] = getLucidPrecodesFromUSHHINumberRange(value)
        break
      }
      case AudienceCriteriaType.AGE: {
        const value = audience.criteria[criteriaType]
        if (!value) break
        lucid[pqTypes[criteriaType].lucidKey] = getLucidPrecodesFromNumberRange(value)
        break
      }
      default: {
        const value = audience.criteria[criteriaType]
        if (value === undefined) break
        const criteriaTypeInfo = pqTypes[criteriaType]
        lucid[criteriaTypeInfo.lucidKey] = getLucidPrecodesFromOptionValues(criteriaTypeInfo, value)
        break
      }
    }
  }

  return {
    name: audience.name,
    segment: {
      custom: audience.criteria[AudienceCriteriaType.ADDITIONAL] || false,
      countries: geographicLocation && geographicLocation.type === GeographicLocationType.INTERNATIONAL
        ? geographicLocation.countries
        : undefined,
      lucid
    }
  }
}

export function clampUSHHINumberRange (numberRange: NumberRange): NumberRange {
  let [start, end] = numberRange

  if (start < 25000) start = 0
  else if (start > 100000) start = 100000

  if (end < 25000) end = 25000
  else if (end > 100000) end = Infinity

  return [start, end]
}

export function parseLucidHHIPrecodeRange (values: string[]): NumberRange {
  const valueNumbers = values.map(v => parseInt(v))
  const minPrecode = min(valueNumbers)
  const maxPrecode = max(valueNumbers)
  if (minPrecode === undefined || maxPrecode === undefined) return [0, Infinity]

  const minHHI = parseLucidHHIPrecode(minPrecode)[0]
  const maxHHI = parseLucidHHIPrecode(maxPrecode)[1]
  return [minHHI, maxHHI]
}

function parseLucidHHIPrecode (precode: number): NumberRange {
  const start = lucidUSHHIBucketOffsets[precode - 1] ?? 0
  const end = lucidUSHHIBucketOffsets[precode] === undefined ? Infinity : lucidUSHHIBucketOffsets[precode] - 1
  return [start, end]
}

function formatUSCurrency (value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

export function formatUSHHINumberRange (range: NumberRange): string {
  const [start, end] = range

  if (start > 0) {
    if (end === Infinity) {
      return `At least ${formatUSCurrency(start)}`
    } else {
      return `${formatUSCurrency(start)} - ${formatUSCurrency(end)}`
    }
  } else {
    if (end === Infinity) {
      return 'Any amount'
    } else {
      return `Up to ${formatUSCurrency(end)}`
    }
  }
}

export const USAdultsAudience: Audience = {
  id: '5da633a1d604b500042e1fb1',
  name: 'US Adults',
  criteria: {
    [AudienceCriteriaType.AGE]: [18, 75],
    [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: {
      type: GeographicLocationType.USA
    }
  }
}

function isSelectable (audience: GraphAudience): boolean {
  return audience.payload.account_id !== 'alpha_featured' &&
    !audience.payload.archived &&
    audience.payload.segment?.visible !== false
}

export function getSelectableAudiences (workspaceAudiences: GraphAudience[]) {
  return workspaceAudiences.filter(isSelectable).map(parseGraphAudience)
}

export enum CriteriaGroupType {
  DEMOGRAPHIC = 'demographic',
  PROFESSIONAL = 'professional',
  OTHER = 'other'
}

interface CriteriaGroup {
  title: String
  type: CriteriaGroupType
  dividerIndex?: number
  criteriaTypes: AudienceCriteriaType[]
}

export const criteriaGroups: CriteriaGroup[] = [
  {
    title: 'Demographic Filters',
    type: CriteriaGroupType.DEMOGRAPHIC,
    dividerIndex: 1,
    criteriaTypes: [
      AudienceCriteriaType.AGE,
      AudienceCriteriaType.GEOGRAPHIC_LOCATION,
      AudienceCriteriaType.EDUCATION_LEVEL,
      AudienceCriteriaType.GENDER,
      AudienceCriteriaType.US_HHI,
      AudienceCriteriaType.PARENTAL_STATUS,
      AudienceCriteriaType.RELATIONSHIP_STATUS,
      AudienceCriteriaType.HOUSEHOLD_TYPE
    ]
  },
  {
    title: 'Professional Filters',
    type: CriteriaGroupType.PROFESSIONAL,
    criteriaTypes: [
      AudienceCriteriaType.EMPLOYMENT,
      AudienceCriteriaType.INDUSTRY,
      AudienceCriteriaType.NUMBER_OF_EMPLOYEES,
      AudienceCriteriaType.STANDARD_COMPANY_DEPARTMENT,
      AudienceCriteriaType.JOB_TITLE,
      AudienceCriteriaType.B2B_DECISIONS
    ]
  },
  {
    title: 'Other Filters',
    type: CriteriaGroupType.OTHER,
    criteriaTypes: [
      AudienceCriteriaType.DEVICE,
      AudienceCriteriaType.BIG_PURCHASES
    ]
  }
]

export const hhiSelectOptions: [Array<SelectOption>, Array<SelectOption>] = [
  [{ label: '$0', value: 0 },
    { label: '$25,000', value: 25000 },
    { label: '$30,000', value: 30000 },
    { label: '$35,000', value: 35000 },
    { label: '$40,000', value: 40000 },
    { label: '$45,000', value: 45000 },
    { label: '$50,000', value: 50000 },
    { label: '$55,000', value: 55000 },
    { label: '$60,000', value: 60000 },
    { label: '$65,000', value: 65000 },
    { label: '$75,000', value: 75000 },
    { label: '$80,000', value: 80000 },
    { label: '$85,000', value: 85000 },
    { label: '$90,000', value: 90000 },
    { label: '$95,000', value: 95000 },
    { label: '$100,000', value: 100000 }
  ],
  [{ label: '$24,999', value: 24999 },
    { label: '$29,999', value: 29999 },
    { label: '$34,999', value: 34999 },
    { label: '$39,999', value: 39999 },
    { label: '$44,999', value: 44999 },
    { label: '$49,999', value: 49999 },
    { label: '$54,999', value: 54999 },
    { label: '$59,999', value: 59999 },
    { label: '$64,999', value: 64999 },
    { label: '$69,999', value: 69999 },
    { label: '$74,999', value: 74999 },
    { label: '$79,999', value: 79999 },
    { label: '$84,999', value: 84999 },
    { label: '$89,999', value: 89999 },
    { label: '$94,999', value: 94999 },
    { label: '$99,999', value: 99999 },
    { label: '>$100,000', value: Infinity }
  ]
]

export const educationLevelOptions = [
  'No Degree',
  'High School',
  'Associates',
  'Bachelors',
  'Graduate'
]

export const householdTypeOptions = [
  'Rented apartment',
  'Owned apartment',
  'Rented house',
  'Owned house',
  'Farm',
  'University residence',
  'Living with parents',
  'Other'
]

export const parentalStatusOptions =
  [
    'Pregnant/expecting a child within the next 9 months',
    'One or more children under the age of 18 living in household',
    'One or more children aged 18 or older living in household',
    'No children living in household and not pregnant/expecting a child within the next 9 months'
  ]

export const relationshipStatusOptions =
  [
    'Single, never married',
    'Married',
    'Seperated, divorced or widowed',
    'Domestic partnership/living with someone',
    'Prefer not to say'
  ]

export const employmentOptions =
  [
    'Full Time',
    'Part Time',
    'Student',
    'Unemployed',
    'Retired',
    'Other'
  ]

export const employeeCountOptions =
  [
    '1',
    '2-10',
    '11-50',
    '51-100',
    '101-500',
    '501-1000',
    '1001-5000',
    'Greater than 5000',
    "I don't work/I don't know"
  ]

export const jobTitleOptions =
  [
    'C-Level, Owner, Partner, President',
    'Vice President',
    'Director',
    'Manager',
    'Analyst',
    'Assistant or Associate',
    'Administrative',
    'Consultant',
    'Other'
  ]

export const deviceOptions =
  [
    'Mobile or Tablet',
    'Desktop or Laptop'
  ]

export const bigPurchaseOptions =
  [
    'Household appliances',
    'TV',
    'Mobile phone',
    'Laptop',
    'Car',
    'Flight ticket',
    'House',
    'None of the above'
  ]

export const companyDeptOptions =
  [
    'Administration/General Staff',
    'Customer Service/Client Service',
    'Executive Leadership',
    'Finance/Accounting',
    'Human Resources',
    'Legal/Law',
    'Marketing',
    'Operations',
    'Procurement',
    'Sales/Business Development',
    'Technology Development Hardware (not only IT)',
    'Technology Development Software (not only IT)',
    'Technology Implementation',
    'Other',
    "I don't work"
  ]

export const b2bDecisionOptions =
  [
    'IT Hardware',
    'IT Software',
    'Printers and copiers',
    'Financial Department',
    'Human Resources',
    'Office supplies',
    'Corporate travel',
    'Telecommunications',
    'Sales',
    'Shipping',
    'Operations',
    'Legal services',
    'Marketing / Advertising',
    'Security',
    'Food services',
    'Auto leasing / purchasing',
    'Procurement',
    'Other',
    "I don't have influence or decision making authority"
  ]

export const industryOptions: FilterOptionGroups[] =
  [
    {
      order: 'A',
      titles: [{ name: 'Accounting' },
        { name: 'Advertising' },
        { name: 'Agriculture/Fishing' },
        { name: 'Architecture' },
        { name: 'Automotive' },
        { name: 'Aviation' }
      ]
    },
    {
      order: 'B',
      titles: [{ name: 'Banking/Financial' },
        { name: 'Bio-Tech' },
        { name: 'Brokerage' }
      ]
    },
    {
      order: 'C',
      titles: [{ name: 'Carpenting/Electrical installations/VVS' },
        { name: 'Chemicals/Plastics/Rubber' },
        { name: 'Communications/Information' },
        { name: 'Computer Hardware/Software' },
        { name: 'Construction' },
        { name: 'Consulting' },
        { name: 'Consumer Electronics' },
        { name: 'Consumer Packaged Goods' }
      ]
    },
    {
      order: 'E',
      titles: [{ name: 'Education' },
        { name: 'Energy/Utilities/Oil and Gas' },
        { name: 'Engineering' },
        { name: 'Environmental Services' }
      ]
    },
    {
      order: 'F',
      titles: [{ name: 'Fashion/Apparel' },
        { name: 'Food/Beverage' }
      ]
    },
    {
      order: 'G',
      titles: [{ name: 'Government/Public Sector' }
      ]
    },
    {
      order: 'H',
      titles: [{ name: 'Healthcare' },
        { name: 'Hospitality/Tourism' },
        { name: 'Human Resources' }
      ]
    },
    {
      order: 'I',
      titles: [{ name: 'Information Technology/IT' },
        { name: 'Insurance' },
        { name: 'Internet' },
        { name: "I don't work" }
      ]
    },
    {
      order: 'L',
      titles: [{ name: 'Legal/Law' }
      ]
    },
    {
      order: 'M',
      titles: [{ name: 'Manufacturing' },
        { name: 'Market Research' },
        { name: 'Marketing' },
        { name: 'Media/Entertainment' },
        { name: 'Military' }
      ]
    },
    {
      order: 'N',
      titles: [{ name: 'Non Profit/Social services' }
      ]
    },
    {
      order: 'P',
      titles: [{ name: 'Personal Services' },
        { name: 'Pharmaceuticals' },
        { name: 'Printing Publishing' },
        { name: 'Public Relations' }
      ]
    },
    {
      order: 'R',
      titles: [{ name: 'Real Estate/Property' },
        { name: 'Retail/Wholesale trade' }
      ]
    },
    {
      order: 'S',
      titles: [{ name: 'Sales' },
        { name: 'Security' },
        { name: 'Shipping/Distribution' }
      ]
    },
    {
      order: 'T',
      titles: [{ name: 'Telecommunications' },
        { name: 'Transportation' }
      ]
    },
    {
      order: 'O',
      titles: [{ name: 'Other' }
      ]
    }
  ]

export const cardDetails: FilterDetails = {
  'Additional Criteria': {
    name: 'Custom Criteria',
    type: 'custom',
    description: 'Create your own filter criteria to reach your target audience',
    screener: "We'll create screener questions based on the description that you provide. Keep in mind that each criteria you add may narrow your audience further and could impact the number of responses you'll receive."
  },
  Education: {
    name: 'Education',
    type: 'multipleStringSelections',
    description: 'Select education level filter criteria',
    screener: 'Screener question: What is the highest level of education you have completed?',
    options: educationLevelOptions

  },
  'Household Income': {
    name: 'Household Income',
    type: 'dropDown',
    description: 'Select the income range that applies to your target audience',
    screener: 'Screener question: How much total combined income do all members of your household earn before taxes?',
    options: hhiSelectOptions
  },
  Gender: {
    name: 'Gender',
    type: 'multipleStringSelections',
    description: 'Select gender filter criteria',
    screener: 'Screener question: What is your gender?',
    options: ['Male', 'Female']

  },
  'Parental Status': {
    name: 'Parental Status',
    type: 'multipleStringSelectionsNotButtons',
    description: 'Select parental status filter criteria',
    screener: 'Screener question: Please choose the options that best describe your household:',
    options: parentalStatusOptions

  },
  'Relationship Status': {
    name: 'Relationship Status',
    type: 'multipleStringSelections',
    description: 'Select relationship status filter criteria',
    screener: 'Screener question: What is your relationship status?',
    options: relationshipStatusOptions

  },
  'Household Type': {
    name: 'Household Type',
    type: 'multipleStringSelections',
    description: 'Select household type filter criteria',
    screener: 'Screener question: What best describes your current household?',
    options: householdTypeOptions
  },
  Employment: {
    name: 'Employment',
    type: 'multipleStringSelections',
    description: 'Select employment status filter criteria',
    screener: 'Screener Question: What is your current employment status?',
    options: employmentOptions
  },
  '# of Employees': {
    name: '# of Employees',
    type: 'multipleStringSelections',
    description: 'Select company size filter criteria',
    screener: 'Screener Question: Approximately how many employees work at your organization (all locations)?',
    options: employeeCountOptions
  },
  'Job Title': {
    name: 'Job Title',
    type: 'multipleStringSelections',
    description: 'Select job title filter criteria',
    screener: 'Screener Question: What is your job title, level or responsibility?',
    options: jobTitleOptions
  },
  Device: {
    name: 'Device',
    type: 'multipleStringSelections',
    description: 'Select device type filter criteria',
    screener: 'Screener Question: What type of device are you using?',
    options: deviceOptions
  },
  'Big Purchases': {
    name: 'Big Purchases',
    type: 'multipleStringSelections',
    description: 'Select big purchase filter criteria',
    screener: 'Screener Question: Which of the following products have you purchased in the last 2 years, or intend to buy in the next 12 months?',
    options: bigPurchaseOptions
  },
  'Company Dept.': {
    name: 'Company Dept.',
    type: 'multipleStringSelections',
    description: 'Select company department filter criteria',
    screener: 'Screener Question: Which department do you primarily work within at your organization?',
    options: companyDeptOptions
  },
  'B2B Decisions': {
    name: 'B2B Decisions',
    type: 'multipleStringSelections',
    description: 'Select decision making authority filter critieria\ni.e. Employee with influence or authority over business spending or purchasing decisions',
    screener: 'Screener Question: Please choose which departments/products you have influence or decision making authority over regarding spending/purchasing.',
    options: b2bDecisionOptions
  },
  Industry: {
    name: 'Industry',
    type: 'multipleSelectDropDown',
    description: 'Select industry type filter criteria',
    screener: 'Screener Question: Which of the following best describes the industry that you, personally, work in?',
    options: industryOptions
  }
}

export type NewAudience = Pick<Audience, 'name' | 'criteria'> & { duplicated?: string } // dupedAudienceId

export enum NewAudienceValidationErrorType {
  NAME_UNDEFINED = 'name_undefined',
  NAME_EXISTS = 'name_exists',
  NO_OPTIONS_SELECTED = 'no_options_selected',
  ALL_OPTIONS_SELECTED = 'all_options_selected'
}

export type NewAudienceValidationError = {
  type: NewAudienceValidationErrorType.NAME_UNDEFINED | NewAudienceValidationErrorType.NAME_EXISTS
} | {
  type: NewAudienceValidationErrorType.NO_OPTIONS_SELECTED | NewAudienceValidationErrorType.ALL_OPTIONS_SELECTED
  criteriaType: AudienceCriteriaType
}

export function validateNewAudience (newAudience: NewAudience, existingAudienceNames: string[]): NewAudienceValidationError[] {
  const errors: NewAudienceValidationError[] = []
  if (!newAudience.name) {
    errors.push({ type: NewAudienceValidationErrorType.NAME_UNDEFINED })
  } else if (existingAudienceNames.includes(newAudience.name)) {
    errors.push({ type: NewAudienceValidationErrorType.NAME_EXISTS })
  }

  const hhiValue = newAudience.criteria[AudienceCriteriaType.US_HHI]
  if (hhiValue !== undefined && hhiValue[0] === 0 && hhiValue[1] === 0) {
    errors.push({
      type: NewAudienceValidationErrorType.NO_OPTIONS_SELECTED,
      criteriaType: AudienceCriteriaType.US_HHI
    })
  }

  const optionValueCriteriaTypes: Exclude<AudienceCriteriaType, AudienceCriteriaType.AGE | AudienceCriteriaType.GEOGRAPHIC_LOCATION | AudienceCriteriaType.ADDITIONAL | AudienceCriteriaType.US_HHI>[] = [
    AudienceCriteriaType.EDUCATION_LEVEL,
    AudienceCriteriaType.GENDER,
    AudienceCriteriaType.PARENTAL_STATUS,
    AudienceCriteriaType.RELATIONSHIP_STATUS,
    AudienceCriteriaType.HOUSEHOLD_TYPE,
    AudienceCriteriaType.EMPLOYMENT,
    AudienceCriteriaType.INDUSTRY,
    AudienceCriteriaType.NUMBER_OF_EMPLOYEES,
    AudienceCriteriaType.STANDARD_COMPANY_DEPARTMENT,
    AudienceCriteriaType.JOB_TITLE,
    AudienceCriteriaType.B2B_DECISIONS,
    AudienceCriteriaType.DEVICE,
    AudienceCriteriaType.BIG_PURCHASES
  ]

  for (const criteriaType of optionValueCriteriaTypes) {
    const value = newAudience.criteria[criteriaType]
    if (value === undefined) continue
    if (value.length === 0) {
      errors.push({ type: NewAudienceValidationErrorType.NO_OPTIONS_SELECTED, criteriaType })
    } else if (value.length === cardDetails[criteriaType].options?.length) {
      errors.push({ type: NewAudienceValidationErrorType.ALL_OPTIONS_SELECTED, criteriaType })
    }
  }

  return errors
}

export const audienceNameMaxLength = 60
